import React, { useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useApiContext } from '../context/ApiContext.jsx';
import { AuthContext } from "../context/AuthContext.jsx";
import { useRoom } from '../context/RoomContext.jsx';
import UserItem from './UserItem.jsx';
import { confirm } from './Confirm.jsx';
import { computeTotalScoreFromQuestions } from '../helpers/ScoreUtils.js';
import { useAnimatedList } from '../hooks/useAnimatedList.js';
import { getDemoRoomStatus } from '../services/RoomStatusService.js';
import { useSfx } from "../hooks/useSfx.jsx";
import { arraysEqual } from '../helpers/objectDiff.js';

const RoomStatus = ({ questions, isFinalStep, handleUserClick }) => {
	const { roomState, setRoomState } = useRoom();
	const { api, isDemo } = useApiContext();
	const { sessionInfo } = useContext(AuthContext);
	const usersRef = useRef([]);
	//	const toastShownRef = useRef(false);
	const sfx = useSfx();

	// --- QUERY PURA (niente side-effects qui)
	const { data: room = { items: [] } } = useQuery({
		// includo anche disableFetch/useDemo tra le chiavi per coerenza cache
		queryKey: ['stato-partecipanti', isFinalStep],
		// niente enabled: vogliamo SEMPRE almeno una fetch
		queryFn: async () => {
			if (isDemo) {
				return await getDemoRoomStatus(api, isFinalStep);
			}

			// REAL MODE 
			const { data } = await api.get('/api/trivia/room-status', {
				params: { sortByScore: Boolean(isFinalStep) },
			});
			return data;
		},
		select: (data) => {
			const allItems = (data?.items ?? []).slice();
			const currentSessionId = sessionInfo?.sessionId ?? null;
			const currentItem = allItems.find(item => currentSessionId && String(item.sessionId) === String(currentSessionId));

			let items = allItems
				.filter((el) => el.type === 1)
				.sort((a, b) => {
					const ia = typeof a.itemIndex === 'number' ? a.itemIndex : 0;
					const ib = typeof b.itemIndex === 'number' ? b.itemIndex : 0;
					return ib - ia;
				});

			// Calculate `isCurrentSession` on the client: server no longer provides this value
			items = items.map(item => ({
				...item,
				isCurrentSession: !!(item.isCurrentSession || (currentSessionId && String(item.sessionId) === String(currentSessionId)))
			}));

			if (currentItem) {
				data.statusCode = currentItem.progressStatusCode;
				data.statusDisplayName = currentItem.progressStatusText;
			}

			// DEMO: in final step, forziamo nickName + avatarId del current player
			if (isDemo && isFinalStep && sessionInfo) {

				const demoTotalScore = computeTotalScoreFromQuestions(questions);
				items = items.map((item) => {
					if (!item.isCurrentSession) return item;

					return {
						...item,
						nickName: sessionInfo.nickName ?? item.nickName,
						avatarId: sessionInfo.currentAvatarId ?? item.avatarId,
						totalScore: demoTotalScore,
						displayName: sessionInfo.nickName ?? item.displayName,
					};
				});

				items = items.sort((a, b) => {
					const ia = typeof a.totalScore === 'number' ? a.totalScore : 0;
					const ib = typeof b.totalScore === 'number' ? b.totalScore : 0;
					return ib - ia;
				});
			}

			return { ...data, items };
		},
		keepPreviousData: true,
		refetchInterval: (query) => {
			// QUI gestiamo il “solo una volta”
			//if (disableFetch) return false; // nessun polling

			const d = query.state.data;
			if (!d?.items?.length) return 5000;
			const allDone = d.items.every((u) => u.isCompleted);
			return allDone ? false : 5000;
		},
		placeholderData: (prev) => prev ?? { items: [] },
	});

	const getRenderedUsers = useCallback((items) => {
		if (!items || !items.length) return [];

		const diffByKey = (A, B, key = 'sessionId') => {
			const idsB = new Set(B.map((o) => o?.[key]));
			return A.filter((o) => !idsB.has(o?.[key]));
		};

		if (!isFinalStep) return items;

		const podium = items.filter((el) => el.isCompleted).slice(0, 3);
		return diffByKey(items, podium);
	}, [isFinalStep]);

	const roomUsers = useAnimatedList(room.items, {
		isFinalStep,
		usersRef,
		getRenderedItems: getRenderedUsers,
		animationDurationInMs: isFinalStep ? 1200 : 800,
	});

	useEffect(() => {
		const items = room?.items ?? [];
		if (!items.length) return;

		const allCompleted = items.every((u) => u.isCompleted);
		const order = items.map((u) => u?.sessionId ?? u?.id ?? '');
		const currentUserPosition = items.findIndex((u) => u.isCurrentSession) + 1;

		// Aggiorna il contesto solo se cambia qualcosa
		setRoomState((prev) => {
			const prevOrder = prev.roomItemsOrder ?? [];
			const nextOrder = order ?? [];
			const orderChanged = !arraysEqual(prevOrder, nextOrder);

			if (prev.status === room.statusCode && prev.allCompleted === allCompleted && !orderChanged) {
				return prev;
			}

			return {
				...prev,
				status: room.statusCode,
				allCompleted,
				currentUserPosition,
				roomItemsOrder: order,
			};
		});
	}, [room.items, room.statusCode]); // <- niente roomState nelle deps

	useEffect(() => {
		const myHandler = (ev) => {
			if (!ev.target.closest(".room-status") && ev.target.id != "room-status-toggler" && !ev.target.closest("#room-status-toggler")) {
				setRoomState(prev => ({ ...prev, isPanelOpen: false }))
			}
		}
		if (roomState.isPanelOpen) {
			document.body.addEventListener('click', myHandler);
			sfx.playSwoosh({ duration: 0.6 })
		} else {
			document.body.removeEventListener('click', myHandler);
		}

		return () => document.body.removeEventListener('click', myHandler)
	}, [roomState.isPanelOpen]);

	const handleActivate = async (ev) => {
		ev.preventDefault();
		const result = await confirm({
			message: "Sei sicuto di voler attivare questa Room?"
		})

		if (result) {
			if (isDemo) {
				toast.info('Demo: attivazione simulata');
				return;
			}
			api.get('/api/trivia/start-all-sessions').then(() => {
				console.log("Room attivata con successo");
				api.get('/api/trivia/room-status', {
					params: { sortByScore: Boolean(isFinalStep) }
				}).then((response) => {
					room.items = response.data.items.filter(el => el.type === 1);
				});
			})
		}
	}

	const completedRoomUsers = roomUsers.filter((u) => u?.isCompleted === true);
	const pendingRoomUsers = roomUsers.filter((u) => !u?.isCompleted);

	return (
		<>
			{room.items?.length > 0 && questions ? (
				<div className={`room-status-container ${roomState.isPanelOpen ? 'open' : ''}`}>
					<div className="room-status">
						{isFinalStep && (
							<>
								{sessionInfo.sessionType === 3 && !sessionInfo.autoStartSurvey && roomState.status == "NotStarted" ? (
									<h2 className="fw-bold d-flex flex-column align-items-center fs-4 mb-6">
										<div className="mb-3">
											<i className="bi bi-exclamation-triangle-fill me-2"></i>
											Questa partita deve essere attivata manualmente dal docente.
										</div>

										<a href="#" className="btn btn-primary btn-lg px-5" onClick={(ev) => { handleActivate(ev) }}>Attiva sessione di gioco</a>
									</h2>
								) : ""}
								{room.items.filter(el => el.isCompleted).slice(0, 3).length ? (
									<>
										<h2 className="fw-bold d-flex align-items-center mb-3">
											<i className="bi bi-stars fs-3 me-3 mb-sm-0"></i>
											Il podio
										</h2>
										<div className="room-status-podium">
											{room.items.filter(el => el.isCompleted).slice(0, 3).map((el, index) => {
												//if (!isFinalStep && el.isCurrentSession) return null;
												return <UserItem
													handleUserClick={sessionInfo.sessionType === 3 ? handleUserClick : null}
													key={el.id ?? index}
													user={el}
													usersRef={usersRef}
													index={index}
													isPodium={true}
													isFinalStep={isFinalStep}
													questions={questions}
												/>
											})}

										</div>
									</>
								) : ""}

							</>
						)}

						{completedRoomUsers?.length ? 
							<>
								<h2 className={`fw-bold d-flex align-items-center mb-4 ${!isFinalStep ? "d-none" : "" }`}>
									<i className="bi bi-bar-chart-fill fs-3 me-3"></i>
									I giocatori che hanno completato la partita
								</h2>
								<div className="user-items-list">
									{completedRoomUsers.map((el, index) => {
										return <UserItem
											handleUserClick={sessionInfo.sessionType === 3 ? handleUserClick : null}
											key={el.id ?? el.sessionId ?? `completed-${index}`}
											user={el}
											usersRef={usersRef}
											index={index}
											isFinalStep={isFinalStep}
											questions={questions}
										>
											<h3>{el.isCompleted}</h3>
											<div className="user-item-ranking">{index + 1 + room.items.filter(el => el.isCompleted).slice(0, 3).length}</div>
										</UserItem>
									})}
								</div>
							</>
						: ""}
						
						{pendingRoomUsers?.length ? (
							<>
								<h2 className={`fw-bold d-flex align-items-center mt-7 mb-4 ${!isFinalStep ? "d-none" : "" }`}>
									<i className="bi bi-bar-chart fs-3 me-3"></i>
									Gli altri giocatori
								</h2>
								<div className="user-items-list">

									{pendingRoomUsers.map((el, index) => {
										const offset = completedRoomUsers.length;
										return <UserItem
											handleUserClick={sessionInfo.sessionType === 3 ? handleUserClick : null}
											key={el.id ?? el.sessionId ?? `pending-${index}`}
											user={el}
											usersRef={usersRef}
											index={index + offset}
											isFinalStep={isFinalStep}
											questions={questions}
										>
											<h3>{el.isCompleted}</h3>
											{/*<div className="user-item-ranking">{index + 1 + offset + room.items.filter(el => el.isCompleted).slice(0, 3).length}</div>*/}
										</UserItem>
									})}
								</div>
							</>

						) : ""}
					</div>

					<a
						href="#"
						id="room-status-toggler"
						className={`btn btn-primary px-0 text-decoration-none ${roomState.isPanelOpen ? 'open' : ''}`}
						style={{ backgroundColor: "var(--game-header-bg-color)" }}
						onClick={(ev) => {
							ev.preventDefault();
							setRoomState(prev => ({ ...prev, isPanelOpen: !prev.isPanelOpen }));
						}}
					>
						<i className={`bi bi-chevron-compact-${roomState.isPanelOpen ? 'left' : 'right'}`}></i>
					</a>
				</div>
			) : null}
		</>
	);
};

export default RoomStatus;
